/**
 * The week highlighter's controls — the generator's `#weekbar` and `#week-note`.
 *
 * The generator reached into the DOM to toggle `.wk-hl` on each chip and to
 * write the readout. Here the selected week index is React state held by
 * BlueprintView, the highlighted set is derived from it, and both this bar and
 * every chip re-render from that. Nothing calls document.querySelector — the
 * DOM-patching approach is the bug CLAUDE.md records, and it is not reintroduced.
 *
 * The count word comes from useTerms(). States take no plural but nouns do, and
 * the plural is looked up rather than suffixed.
 */
import { useTerms } from '../../lib/terms';
import { dayFmt, type Week } from './weeks';

export interface WeekSummary {
  week: Week | null;
  /** How many deliverables the selected week outlines. */
  count: number;
  /** De-duplicated names of the schedule items active that week. */
  names: string[];
}

function weekLabel(week: Week): string {
  return `Week of ${dayFmt(week.start)} – ${dayFmt(week.end)} ${week.end.slice(0, 4)}`;
}

export function WeekBar({
  weeks,
  selected,
  summary,
  onSelect,
  onClear,
}: {
  weeks: Week[];
  /** Index into `weeks`, or -1 for no highlight. */
  selected: number;
  summary: WeekSummary;
  onSelect: (index: number) => void;
  onClear: () => void;
}) {
  const terms = useTerms();

  // No schedule rows means no week control at all, exactly as in the generator.
  if (!weeks.length) return null;

  const { week, count, names } = summary;
  const noun = count === 1 ? terms.noun('deliverable') : terms.plural('deliverable');

  return (
    <div className="weekbar">
      <span className="lbl" id="weekbar-label">
        Highlight week
      </span>
      <input
        type="range"
        min={0}
        max={weeks.length - 1}
        step={1}
        value={selected < 0 ? 0 : selected}
        aria-labelledby="weekbar-label"
        onChange={(e) => onSelect(Number(e.target.value))}
      />
      <span className="readout" aria-live="polite">
        {week ? (
          <>
            <b>{weekLabel(week)}</b> — {count} {noun.toLowerCase()} outlined
            <span className="rooms">{names.length ? names.join(', ') : 'nothing scheduled'}</span>
          </>
        ) : (
          <>
            <b>No week highlighted</b> — move the slider to pick one.
          </>
        )}
      </span>
      <button type="button" className="btn-clear" onClick={onClear}>
        Clear
      </button>
    </div>
  );
}

/** The same summary repeated inside the sheet, so it survives into the printed page. */
export function WeekNote({ summary }: { summary: WeekSummary }) {
  const terms = useTerms();
  const { week, count, names } = summary;
  if (!week) return null;

  const noun = count === 1 ? terms.noun('deliverable') : terms.plural('deliverable');
  const who = names.length ? names.join(', ') : 'nothing scheduled';

  return (
    <div className="week-note">
      {weekLabel(week)} — {count} {noun.toLowerCase()} highlighted · {who}
    </div>
  );
}
